import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  computed,
  inject,
  OnInit,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslocoDirective, TranslocoService } from '@jsverse/transloco';

import { AdminOperationTemplateService } from './services/admin-operation-template.service';
import {
  CreateOperationProfileRequest,
  Operation,
  OperationProfile,
  OperationProfileRequest,
  OperationRequest,
} from '../operation-profile/models/operation-profile.model';
import { ProfileDialogComponent, DialogMode } from '../operation-profile/dialogs/profile-dialog.component';
import { OperationDialogComponent } from '../operation-profile/dialogs/operation-dialog.component';
import { ToastService } from '../../shared/services/toast-service';
import { ModalService } from '../../shared/components/modal/modal.service';
import { LanguageService } from '../../core/i18n/language.service';
import { Grid } from '../../shared/components/grid/grid';
import { GridPage } from '../../shared/components/grid/grid-page';
import { GridCellDirective } from '../../shared/components/grid/directives/grid-cell.directive';
import { GridDetailDirective } from '../../shared/components/grid/directives/grid-detail.directive';
import { GridColumn, GridSort, GridTab, gridColumns } from '../../shared/components/grid/models/grid.model';
import { forkJoin, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { Icon } from '../../shared/components/icon/icon';
import { ScheduleStrip, ScheduleBand } from '../../shared/components/schedule-strip/schedule-strip';
import { IlluminationBar } from '../../shared/components/illumination-bar/illumination-bar';
import { PowerThresholdBar } from '../../shared/components/power-threshold-bar/power-threshold-bar';
import { LabelBadge, LabelBadgeDot } from '../../shared/components/label-badge/label-badge';
import { Badge } from '../../shared/components/badge/badge';
import { Button } from '../../shared/components/button/button';
import { StackedBar, StackedBarSegment } from '../../shared/components/stacked-bar/stacked-bar';
import { TemplateAdoptionResponse } from './models/template-adoption.model';
import { TemplateVersionResponse } from './models/template-version.model';

/** Detail-panel tab within the per-template inspector (Templates tab). */
type DetailTab = 'overview' | 'operations';

/** Grid-level tab: what the single grid shows. Templates = template rows +
 *  inspector; Adoption = one row per template; History = one row per version
 *  across all templates. Mirrors the software-updates [tabs] pattern. */
type GridView = 'templates' | 'adoption' | 'history';

/** A flattened version row for the History grid (version + its template name).
 *  `isHeader` marks the template's latest version (the always-visible accordion
 *  header with the caret); `isChild` marks a past version shown only while the
 *  template is expanded. `siblingCount` is the template's total version count,
 *  shown on the header. */
interface VersionGridRow extends TemplateVersionResponse {
  templateId: number;
  templateName: string;
  isHeader: boolean;
  isChild: boolean;
  siblingCount: number;
}

/** A template's version history, newest first, for the History accordion. */
interface VersionGroup {
  templateId: number;
  templateName: string;
  versions: TemplateVersionResponse[];
}

/**
 * Admin screen for platform operation-profile templates. Mirrors the tenant
 * OperationsComponent master/detail, minus the inheritance/override/fork UI
 * (templates have none). The admin API has no per-operation endpoints, so every
 * operation add/edit/delete re-PUTs the whole template via updateTemplate().
 */
@Component({
  selector: 'admin-operation-templates',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    TranslocoDirective,
    Icon,
    ScheduleStrip,
    IlluminationBar,
    PowerThresholdBar,
    Grid,
    GridPage,
    GridCellDirective,
    GridDetailDirective,
    ProfileDialogComponent,
    OperationDialogComponent,
    LabelBadge,
    Badge,
    Button,
    StackedBar,
  ],
  templateUrl: './admin-operation-templates.component.html',
})
export class AdminOperationTemplatesComponent implements OnInit {
  private readonly service = inject(AdminOperationTemplateService);
  private readonly toastService = inject(ToastService);
  private readonly transloco = inject(TranslocoService);
  private readonly languageService = inject(LanguageService);
  private readonly modal = inject(ModalService);
  private readonly destroyRef = inject(DestroyRef);

  /** Full template list (the endpoint is not paginated). */
  readonly templates = signal<OperationProfile[]>([]);
  readonly page = signal(0);
  readonly size = signal(20);
  readonly sort = signal<GridSort>({ sortBy: 'created', sortDirection: 'DESC' });
  readonly isLoading = signal(false);

  /** Which grid tab is active (drives columns + data). */
  readonly activeTab = signal<GridView>('templates');

  /** Master/detail link: the row shown in the inspector. Holds a template in
   *  Templates tab and a version row in History tab. */
  readonly selectedTemplate = signal<OperationProfile | null>(null);
  readonly selectedVersion = signal<VersionGridRow | null>(null);
  readonly detailTab = signal<DetailTab>('overview');

  // Adoption — one row per template, fetched lazily on first entry.
  readonly fleet = signal<TemplateAdoptionResponse[]>([]);
  readonly fleetLoaded = signal(false);
  readonly isFleetLoading = signal(false);

  // History — each template's versions, newest first, fetched lazily. Rendered
  // as an accordion: latest version is a header row; past versions show as
  // children while the template is expanded.
  readonly versionGroups = signal<VersionGroup[]>([]);
  readonly versionsLoaded = signal(false);
  readonly isVersionsLoading = signal(false);
  /** templateIds whose past versions are revealed. */
  readonly expandedTemplates = signal<Set<number>>(new Set());

  // Dialogs + save state
  readonly isProfileDialogOpen = signal(false);
  readonly isProfileSaving = signal(false);
  readonly isOperationDialogOpen = signal(false);
  readonly dialogMode = signal<DialogMode>('add');
  readonly editingTemplate = signal<OperationProfile | null>(null);
  readonly editingOperation = signal<Operation | null>(null);

  readonly templateId = (p: OperationProfile) => String(p.id);
  readonly asTemplate = (row: unknown): OperationProfile => row as OperationProfile;
  readonly asAdoption = (row: unknown): TemplateAdoptionResponse => row as TemplateAdoptionResponse;
  readonly asVersion = (row: unknown): VersionGridRow => row as VersionGridRow;

  /** rowId across all three tabs' row shapes (template id / template id / versionId). */
  readonly rowId = (row: unknown): string => {
    switch (this.activeTab()) {
      case 'adoption':
        return `a-${(row as TemplateAdoptionResponse).templateId}`;
      case 'history':
        return `v-${(row as VersionGridRow).versionId}`;
      default:
        return `t-${(row as OperationProfile).id}`;
    }
  };

  /** Operations of the selected template (rendered in the Operations tab). */
  readonly operations = computed<Operation[]>(() => this.selectedTemplate()?.operations ?? []);

  /** The grid tabs (software-updates pattern: tab bar in the grid toolbar). */
  readonly gridTabs = computed<GridTab[]>(() => {
    this.languageService.current();
    const t = (k: string) => this.transloco.translate(`adminOperationTemplates.modes.${k}`);
    return [
      { id: 'templates', label: t('templates') },
      { id: 'adoption', label: t('adoption') },
      { id: 'history', label: t('history') },
    ];
  });

  /** History accordion rows: each template's latest version as a header row,
   *  followed by its past versions (children) when the template is expanded. */
  readonly historyRows = computed<VersionGridRow[]>(() => {
    const expanded = this.expandedTemplates();
    const rows: VersionGridRow[] = [];
    for (const g of this.versionGroups()) {
      g.versions.forEach((v, i) => {
        const isHeader = i === 0;
        if (!isHeader && !expanded.has(g.templateId)) return;
        rows.push({
          ...v,
          templateId: g.templateId,
          templateName: g.templateName,
          isHeader,
          isChild: !isHeader,
          siblingCount: g.versions.length,
        });
      });
    }
    return rows;
  });

  /** The grid's data for the active tab. */
  readonly gridData = computed<readonly unknown[]>(() => {
    switch (this.activeTab()) {
      case 'adoption':
        return this.fleet();
      case 'history':
        return this.historyRows();
      default:
        return this.pagedTemplates();
    }
  });

  isTemplateExpanded(templateId: number): boolean {
    return this.expandedTemplates().has(templateId);
  }

  toggleTemplateHistory(templateId: number): void {
    this.expandedTemplates.update((set) => {
      const next = new Set(set);
      next.has(templateId) ? next.delete(templateId) : next.add(templateId);
      return next;
    });
  }

  /** Per-template adoption split as stacked-bar segments, for an adoption row. */
  adoptionSegmentsOf(a: TemplateAdoptionResponse): StackedBarSegment[] {
    this.languageService.current();
    return [
      { label: this.t('adoption.upToDate'), count: a.upToDate, tone: 'ok' },
      { label: this.t('adoption.outdated'), count: a.outdated, tone: 'warn' },
      { label: this.t('adoption.notProvisioned'), count: a.notProvisioned, tone: 'off' },
    ];
  }

  /** Client-side sort + page over the full list (the API returns everything). */
  readonly sortedTemplates = computed<OperationProfile[]>(() => {
    const { sortBy, sortDirection } = this.sort();
    const dir = sortDirection === 'ASC' ? 1 : -1;
    const rows = [...this.templates()];
    rows.sort((a, b) => {
      const av = (a as unknown as Record<string, unknown>)[sortBy];
      const bv = (b as unknown as Record<string, unknown>)[sortBy];
      if (av == null) return 1;
      if (bv == null) return -1;
      return av < bv ? -dir : av > bv ? dir : 0;
    });
    return rows;
  });

  readonly pagedTemplates = computed<OperationProfile[]>(() => {
    const start = this.page() * this.size();
    return this.sortedTemplates().slice(start, start + this.size());
  });

  /** Row count for the active tab (drives the grid footer). Adoption/History
   *  show their full lists; only Templates is client-paged. */
  readonly totalElements = computed(() => {
    switch (this.activeTab()) {
      case 'adoption':
        return this.fleet().length;
      case 'history':
        return this.versionGroups().length;
      default:
        return this.templates().length;
    }
  });
  readonly totalPages = computed(() => Math.max(1, Math.ceil(this.totalElements() / this.size())));

  /** Columns switch with the active tab. The grid is typed loosely (unknown
   *  rows); cells cast with asTemplate/asAdoption/asVersion. */
  readonly columns = computed<GridColumn<unknown>[]>(() => {
    this.languageService.current();
    const t = (k: string) => this.transloco.translate(`adminOperationTemplates.columns.${k}`);
    switch (this.activeTab()) {
      case 'adoption':
        return gridColumns<unknown>([
          { key: 'operationProfileId', label: t('template'), type: 'template', template: 'aName', sortable: false, alwaysVisible: true, width: 200 },
          { key: 'adoption', label: t('adoption'), type: 'template', template: 'aBar', sortable: false, width: 320 },
          { key: 'upToDate', label: t('upToDate'), type: 'template', template: 'aUpToDate', sortable: false, width: 100 },
          { key: 'outdated', label: t('outdated'), type: 'template', template: 'aOutdated', sortable: false, width: 100 },
          { key: 'notProvisioned', label: t('notProvisioned'), type: 'template', template: 'aNone', sortable: false, width: 120 },
          { key: 'currentVersion', label: t('version'), type: 'template', template: 'aVersion', sortable: false, width: 90 },
        ]);
      case 'history':
        return gridColumns<unknown>([
          { key: 'templateName', label: t('template'), type: 'template', template: 'hName', sortable: false, alwaysVisible: true, width: 200 },
          { key: 'version', label: t('version'), type: 'template', template: 'hVersion', sortable: false, width: 120 },
          { key: 'created', label: t('published'), type: 'template', template: 'hPublished', sortable: false, width: 190 },
          { key: 'operations', label: t('operations'), type: 'template', template: 'hOps', sortable: false, width: 110 },
        ]);
      default:
        return gridColumns<unknown>([
          { key: 'operationProfileId', label: t('profile'), type: 'template', template: 'profile', sortable: true, alwaysVisible: true, width: 210 },
          { key: 'status', label: t('status'), type: 'template', template: 'status', sortable: false, width: 110 },
          { key: 'powerMode', label: t('powerMode'), type: 'template', template: 'powerMode', sortable: true, width: 110 },
          { key: 'schedule', label: t('schedule'), type: 'template', template: 'schedule', sortable: false, width: 190 },
          { key: 'render', label: t('render'), type: 'text', sortable: false, width: 90, value: (p) => this.rowRender(p as OperationProfile) },
          { key: 'connection', label: t('connection'), type: 'text', sortable: false, width: 100, value: (p) => this.rowConnection(p as OperationProfile) },
          { key: 'week', label: t('week'), type: 'template', template: 'week', sortable: false, width: 170 },
        ]);
    }
  });

  ngOnInit(): void {
    this.loadTemplates();
  }

  private loadTemplates(): void {
    this.isLoading.set(true);
    this.service
      .getTemplates()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response) => {
          this.templates.set(response.data);
          this.ensureSelection(response.data);
          this.isLoading.set(false);
        },
        error: () => {
          this.toastService.show(this.t('toasts.loadFailed'), 'error');
          this.isLoading.set(false);
        },
      });
  }

  onSortChange(sort: GridSort): void {
    this.sort.set(sort);
    this.page.set(0);
  }

  onPageChange(page: number): void {
    this.page.set(page);
  }

  onPageSizeChange(size: number): void {
    this.size.set(size);
    this.page.set(0);
  }

  /** Row click. Templates → inspect the template; History → inspect the version;
   *  Adoption rows aren't clickable (flat metrics). */
  onRowClick(row: unknown): void {
    if (this.activeTab() === 'templates') {
      this.selectedTemplate.set(this.asTemplate(row));
      this.detailTab.set('overview');
    } else if (this.activeTab() === 'history') {
      this.selectedVersion.set(this.asVersion(row));
    }
  }

  /** Keep the Templates detail pane populated: fall back to the first row when
   *  the current selection is gone (deleted) or absent. */
  private ensureSelection(templates: OperationProfile[]): void {
    const current = this.selectedTemplate();
    const stillPresent = current != null && templates.some((d) => d.id === current.id);
    if (stillPresent) {
      this.selectedTemplate.set(templates.find((d) => d.id === current!.id) ?? null);
      return;
    }
    this.selectedTemplate.set(templates[0] ?? null);
  }

  /** In-pane detail tab (Power settings / Operations). */
  setTab(tab: DetailTab): void {
    this.detailTab.set(tab);
  }

  // ── Grid tab switching ──────────────────────────────────────────────────────

  onTabChange(id: string): void {
    const tab = id as GridView;
    this.activeTab.set(tab);
    this.selectedVersion.set(null);
    this.page.set(0);
    if (tab === 'adoption' && !this.fleetLoaded()) this.loadFleet();
    if (tab === 'history' && !this.versionsLoaded()) this.loadVersions();
  }

  /** Fetch fleet adoption once. On error, toast + leave the grid empty. */
  private loadFleet(): void {
    if (this.isFleetLoading()) return;
    this.isFleetLoading.set(true);
    this.service
      .getAdoptionFleet()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response) => {
          this.fleet.set(response.data);
          this.fleetLoaded.set(true);
          this.isFleetLoading.set(false);
        },
        error: () => {
          this.isFleetLoading.set(false);
          this.toastService.show(this.t('adoption.loadFailed'), 'error');
        },
      });
  }

  /** Fetch every template's version history, grouped per template (newest
   *  first). There is no all-versions endpoint, so this fans out per template;
   *  fine at the handful-of-templates scale. A per-template failure drops that
   *  template's versions (caught) rather than failing the whole grid. */
  private loadVersions(): void {
    if (this.isVersionsLoading()) return;
    const templates = this.templates();
    if (templates.length === 0) {
      this.versionsLoaded.set(true);
      return;
    }
    this.isVersionsLoading.set(true);
    forkJoin(
      templates.map((tpl) =>
        this.service.getVersions(tpl.id).pipe(
          map(
            (response): VersionGroup => ({
              templateId: tpl.id,
              templateName: tpl.operationProfileId,
              // Newest first so index 0 is the accordion header.
              versions: [...response.data].sort((a, b) => b.version - a.version),
            }),
          ),
          catchError(() =>
            of({ templateId: tpl.id, templateName: tpl.operationProfileId, versions: [] } as VersionGroup),
          ),
        ),
      ),
    )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (groups) => {
          // Templates with no versions drop out; sort groups by template name.
          const sorted = groups
            .filter((g) => g.versions.length > 0)
            .sort((a, b) => a.templateName.localeCompare(b.templateName));
          this.versionGroups.set(sorted);
          this.versionsLoaded.set(true);
          this.isVersionsLoading.set(false);
        },
        error: () => {
          this.isVersionsLoading.set(false);
          this.toastService.show(this.t('history.loadFailed'), 'error');
        },
      });
  }

  private t(key: string, params?: Record<string, unknown>): string {
    return this.transloco.translate(`adminOperationTemplates.${key}`, params);
  }

  // ── Display helpers (shared with the tenant screen's formatting) ──────────

  private static readonly OP_COLORS = [
    'var(--color-brand)',
    'var(--color-warning)',
    'var(--color-success)',
    'var(--color-caution)',
  ];

  daysLabel(days: string[]): string {
    const dt = (k: string) => this.transloco.translate(`operations.days.${k}`);
    if (!days?.length) return this.transloco.translate('operations.na');
    const norm = days.map((d) => d.slice(0, 3).toUpperCase());
    if (norm.length === 7) return dt('everyDay');
    const weekdays = ['MON', 'TUE', 'WED', 'THU', 'FRI'];
    const weekend = ['SAT', 'SUN'];
    if (norm.length === 5 && weekdays.every((d) => norm.includes(d))) return dt('weekdays');
    if (norm.length === 2 && weekend.every((d) => norm.includes(d))) return dt('weekends');
    return norm.map((d) => d[0] + d.slice(1).toLowerCase()).join(', ');
  }

  windowLabel(op: { startTime: string | null; endTime: string | null }): string {
    const { startTime, endTime } = op;
    if (!startTime || !endTime) return this.transloco.translate('operations.na');
    if (startTime === '00:00' && (endTime === '23:59' || endTime === '24:00')) {
      return this.transloco.translate('operations.window.allDay');
    }
    return `${startTime}-${endTime}`;
  }

  dateRangeLabel(op: { startDate: string | null; endDate: string | null }): string {
    if (!op.startDate) return this.transloco.translate('operations.na');
    const noEnd = this.transloco.translate('operations.settings.noEnd');
    const end = !op.endDate || op.endDate.startsWith('9999') ? noEnd : op.endDate;
    return `${op.startDate} -> ${end}`;
  }

  operationScheduleLabel(op: Operation): string {
    return `${this.daysLabel(op.days)} · ${this.windowLabel(op)} · ${this.dateRangeLabel(op)}`;
  }

  private effectiveOperation(tpl: OperationProfile): Operation | null {
    return tpl.operations?.[0] ?? null;
  }

  rowScheduleLabel(tpl: OperationProfile): string {
    const op = this.effectiveOperation(tpl);
    if (!op) return this.t('row.defaults');
    return `${this.daysLabel(op.days)} · ${this.windowLabel(op)}`;
  }

  rowRender(tpl: OperationProfile): string {
    return this.formatInterval(this.effectiveOperation(tpl)?.renderInterval ?? tpl.renderInterval);
  }
  rowConnection(tpl: OperationProfile): string {
    return this.formatInterval(this.effectiveOperation(tpl)?.connectionInterval ?? tpl.connectionInterval);
  }

  rowFlag(tpl: OperationProfile): 'active' | 'sleep' | 'shutdown' | null {
    const op = this.effectiveOperation(tpl);
    if (!op) return null;
    if (op.shutdown) return 'shutdown';
    if (op.sleep) return 'sleep';
    return 'active';
  }

  flagDot(flag: 'active' | 'sleep' | 'shutdown' | null): 'green' | 'blue' | 'red' | 'gray' {
    switch (flag) {
      case 'active':
        return 'green';
      case 'sleep':
        return 'blue';
      case 'shutdown':
        return 'red';
      default:
        return 'gray';
    }
  }

  powerModeDot(mode: string): 'blue' | 'yellow' {
    return this.powerModeColor(mode) === 'blue' ? 'blue' : 'yellow';
  }

  batteryStatusDot(status: string): LabelBadgeDot | null {
    switch (this.batteryStatusColor(status)) {
      case 'green':
        return 'green';
      case 'orange':
        return 'orange';
      case 'red':
        return 'red';
      default:
        return null;
    }
  }

  formatInterval(seconds: number | null | undefined): string {
    if (seconds == null) return '-';
    if (seconds < 60) return `${seconds} s`;
    const m = seconds / 60;
    return `${Number.isInteger(m) ? m : m.toFixed(1)} min`;
  }

  /** Locale-aware published-date label for a version row. Reads
   *  `languageService.current()` so it re-renders on a language switch. */
  publishedDate(iso: string): string {
    const lang = this.languageService.current();
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return new Intl.DateTimeFormat(lang, { dateStyle: 'medium', timeStyle: 'short' }).format(d);
  }

  operationsCount(tpl: OperationProfile): number {
    return tpl.operations?.length ?? 0;
  }

  illuminationOf(op: Operation): number {
    return op.illumination ?? 0;
  }

  operationColor(index: number): string {
    return AdminOperationTemplatesComponent.OP_COLORS[index % AdminOperationTemplatesComponent.OP_COLORS.length];
  }

  coverageBands(): ScheduleBand[] {
    return this.bandsFrom(this.operations());
  }

  templateBands(tpl: OperationProfile): ScheduleBand[] {
    return this.bandsFrom(tpl.operations ?? []);
  }

  private bandsFrom(ops: Operation[]): ScheduleBand[] {
    return ops.map((op, i) => ({
      days: op.days,
      startTime: op.startTime,
      endTime: op.endTime,
      color: this.operationColor(i),
      sleep: op.sleep,
    }));
  }

  powerModeColor(mode: string): 'blue' | 'neutral' {
    return mode?.toUpperCase() === 'MAIN' ? 'blue' : 'neutral';
  }

  batteryStatusColor(status: string): 'green' | 'orange' | 'red' | 'neutral' {
    switch (status?.toUpperCase()) {
      case 'NORMAL':
        return 'green';
      case 'LOW_POWER':
        return 'orange';
      case 'NO_POWER':
        return 'red';
      default:
        return 'neutral';
    }
  }

  batteryStatusLabel(status: string): string {
    return status
      .split('_')
      .map((w) => w[0] + w.slice(1).toLowerCase())
      .join(' ');
  }

  // ── Template (profile) CRUD + dialog ──────────────────────────────────────

  openAddDialog(): void {
    this.editingTemplate.set(null);
    this.dialogMode.set('add');
    this.isProfileDialogOpen.set(true);
  }

  editSelectedTemplate(): void {
    const tpl = this.selectedTemplate();
    if (!tpl) return;
    this.editingTemplate.set(tpl);
    this.dialogMode.set('edit');
    this.isProfileDialogOpen.set(true);
  }

  async deleteSelectedTemplate(): Promise<void> {
    const tpl = this.selectedTemplate();
    if (!tpl) return;
    const confirmed = await this.modal.confirm({
      kind: 'destructive',
      destructive: true,
      title: this.t('deleteConfirm.title'),
      message: this.t('deleteConfirm.body', { name: tpl.operationProfileId }),
      confirmLabel: this.t('deleteConfirm.confirm'),
      confirmWord: tpl.operationProfileId,
    });
    if (confirmed) this.deleteTemplate(tpl);
  }

  closeProfileDialog(): void {
    this.isProfileDialogOpen.set(false);
    this.editingTemplate.set(null);
  }

  handleProfileSave(request: OperationProfileRequest): void {
    const tpl = this.editingTemplate();
    this.isProfileSaving.set(true);
    if (this.dialogMode() === 'edit' && tpl) {
      // Full-replace PUT: keep the existing operations so editing profile-level
      // fields does not wipe the template's operations.
      const payload: CreateOperationProfileRequest = {
        ...(request as CreateOperationProfileRequest),
        operations: tpl.operations.map(toOperationRequest),
      };
      this.service
        .updateTemplate(tpl.id, payload)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: (response) => this.onSaved(response.data, 'toasts.updated'),
          error: () => this.onSaveError('toasts.updateFailed'),
        });
    } else {
      const payload: CreateOperationProfileRequest = {
        ...(request as CreateOperationProfileRequest),
        operations: [],
      };
      this.service
        .createTemplate(payload)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: (response) => {
            this.isProfileSaving.set(false);
            this.toastService.show(this.t('toasts.created'), 'success');
            this.closeProfileDialog();
            this.templates.update((list) => [response.data, ...list]);
            this.selectedTemplate.set(response.data);
          },
          error: () => this.onSaveError('toasts.createFailed'),
        });
    }
  }

  private deleteTemplate(tpl: OperationProfile): void {
    this.service
      .deleteTemplate(tpl.id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.toastService.show(this.t('toasts.deleted'), 'success');
          this.templates.update((list) => list.filter((d) => d.id !== tpl.id));
          if (this.selectedTemplate()?.id === tpl.id) {
            this.selectedTemplate.set(this.templates()[0] ?? null);
          }
        },
        error: () => this.toastService.show(this.t('toasts.deleteFailed'), 'error'),
      });
  }

  // ── Operation CRUD (immediate full re-PUT of the template) ────────────────

  openAddOperationDialog(): void {
    this.editingOperation.set(null);
    this.dialogMode.set('add');
    this.isOperationDialogOpen.set(true);
  }

  editOperation(operation: Operation): void {
    this.editingOperation.set(operation);
    this.dialogMode.set('edit');
    this.isOperationDialogOpen.set(true);
  }

  closeOperationDialog(): void {
    this.isOperationDialogOpen.set(false);
    this.editingOperation.set(null);
  }

  handleOperationSave(request: OperationRequest): void {
    const tpl = this.selectedTemplate();
    if (!tpl) return;
    const existing = tpl.operations.map(toOperationRequest);
    const editing = this.editingOperation();

    // operationId is the business key; templates' operations round-trip on it.
    const nextOps =
      this.dialogMode() === 'edit' && editing
        ? existing.map((o) => (o.operationId === editing.operationId ? request : o))
        : [...existing, request];

    this.putOperations(tpl, nextOps, 'toasts.operationSaved', 'toasts.operationSaveFailed');
  }

  deleteOperation(operation: Operation): void {
    const tpl = this.selectedTemplate();
    if (!tpl) return;
    const nextOps = tpl.operations
      .filter((o) => o.operationId !== operation.operationId)
      .map(toOperationRequest);
    this.putOperations(tpl, nextOps, 'toasts.operationDeleted', 'toasts.operationDeleteFailed');
  }

  private putOperations(
    tpl: OperationProfile,
    operations: OperationRequest[],
    successKey: string,
    errorKey: string,
  ): void {
    const payload: CreateOperationProfileRequest = {
      operationProfileId: tpl.operationProfileId,
      powerMode: tpl.powerMode,
      lowPowerPercentage: tpl.lowPowerPercentage,
      lowPowerPercentageOut: tpl.lowPowerPercentageOut,
      noPowerPercentage: tpl.noPowerPercentage,
      noPowerPercentageOut: tpl.noPowerPercentageOut,
      renderInterval: tpl.renderInterval,
      connectionInterval: tpl.connectionInterval,
      allowWakeup: tpl.allowWakeup,
      operations,
    };
    this.service
      .updateTemplate(tpl.id, payload)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response) => {
          this.toastService.show(this.t(successKey), 'success');
          this.closeOperationDialog();
          this.applyUpdated(response.data);
        },
        error: () => this.toastService.show(this.t(errorKey), 'error'),
      });
  }

  private onSaved(updated: OperationProfile, successKey: string): void {
    this.isProfileSaving.set(false);
    this.toastService.show(this.t(successKey), 'success');
    this.closeProfileDialog();
    this.applyUpdated(updated);
  }

  private onSaveError(errorKey: string): void {
    this.isProfileSaving.set(false);
    this.toastService.show(this.t(errorKey), 'error');
  }

  /** Apply a server-returned template to the list + selection (optimistic). */
  private applyUpdated(updated: OperationProfile): void {
    this.templates.update((list) => list.map((d) => (d.id === updated.id ? updated : d)));
    if (this.selectedTemplate()?.id === updated.id) {
      this.selectedTemplate.set(updated);
    }
  }
}

/** Strip the response-only fields so an Operation can be re-sent in a PUT. */
function toOperationRequest(op: Operation): OperationRequest {
  return {
    operationId: op.operationId,
    sleep: op.sleep,
    shutdown: op.shutdown,
    renderInterval: op.renderInterval,
    connectionInterval: op.connectionInterval,
    illumination: op.illumination,
    startDate: op.startDate,
    endDate: op.endDate,
    startTime: op.startTime,
    endTime: op.endTime,
    days: op.days,
    batteryPowerStatusList: op.batteryPowerStatusList,
  };
}
